/**
 * 브랜드 허브 상세의 구역마다 제목 아래 한 줄(`hint` — 수·기간만)과 ⓘ 툴팁 줄(`info` — 기준·정렬).
 *
 * ⚠️ 광고·상품의 `{M}개` 는 **포맷을 거치지 않은 수**다 — 다른 수와 달리 `fNumber` 를 덧대지 않는다.
 * 🔑 원장마다 못 거는 축의 말은 `brand-hub-scope-note` 가 짓는다 — 여기서는 구역마다 엮기만 한다.
 */

import type { BrandQualityFlag } from 'src/utils/brand/brand-quality-doc';

import { fNumber } from 'src/utils/format/format-number';

import { BRAND_HUB_FACE_CARDS } from '../constants/brand-hub-face-size';
import { brandHubAdInfo, brandHubMissInfo, brandHubProductInfo } from './brand-hub-scope-note';

import type { BrandHubScopeOn } from './brand-hub-scope-note';
import type { BrandHubProductRegion } from './brand-hub-product-region';

/** ⓘ 툴팁에 이름을 펼쳐 적는 순위 제외 브랜드 수 — 넘치면 「외 N개」 */
const PEER_EXCLUDED_NAMES = 5;

/** 관련 컨텐츠 — 「{총수}건 중 {편 수}개」 */
export function brandHubContentHint(total: number, shown: number): string {
  return `${fNumber(total)}건 중 ${fNumber(shown)}개`;
}

/** 관련 컨텐츠가 비었을 때 — 채널을 골랐으면 그 채널 탓이라고 적는다 */
export function brandHubContentEmptyNote(channelPicked: boolean): string {
  return channelPicked ? '이 채널에는 글이 없습니다.' : '아직 볼 만한 글이 없습니다.';
}

/**
 * 크리에이터 표의 수 — 거름이 걸렸으면 「100명 중 12명」, 아니면 「상위 100명」.
 * ⚠️ **상위 몇 명인지 밝힌다** — 표는 받은 사람까지라 「전부」로 읽히면 안 된다.
 */
export function brandHubCreatorsHint(total: number, shown: number, narrowed: number): string {
  return narrowed > 0 ? `${fNumber(total)}명 중 ${fNumber(shown)}명` : `상위 ${fNumber(total)}명`;
}

/** 광고·상품 구역 — 「{총수}개 중 {M}개」 */
export function brandHubShownHint(total: number, shown: number): string {
  return `${fNumber(total)}개 중 ${shown}개`;
}

/** 광고 구역 ⓘ — 개요의 맛보기와 광고 면이 같이 쓴다(집행 중 · 오래 집행한 순) */
export function brandHubAdsInfo(on: BrandHubScopeOn): string[] {
  return ['집행 중 광고 · 오래 집행한 순', ...brandHubAdInfo(on)];
}

/** 개요 「광고로 무엇을 말하나」 ⓘ — 요약은 소재를 묶기 전 광고 수로 센다 */
export function brandHubAdStatsInfo(on: BrandHubScopeOn): string[] {
  return ['광고 단위 집계 — 같은 소재도 광고마다 따로 셈', ...brandHubAdInfo(on)];
}

/**
 * 상품 구역 ⓘ — 담긴 갈래(랭킹 진입·검색 확인) · 정렬 · 원장 기준.
 * @param kindNote `brandHubProductKindNote` 의 값 — 비면 줄을 두지 않는다
 * @param order 이 구역의 정렬 — 개요 맛보기는 「랭킹 높은 순」, 상품 면은 「관련도순」
 */
export function brandHubProductsInfo(on: BrandHubScopeOn, region: BrandHubProductRegion, order: string, kindNote = ''): string[] {
  return [kindNote, order, ...brandHubProductInfo(on, region)].filter(Boolean);
}

/** 개요 「어디서 얼마에 파나」 ⓘ — 평균 할인율만은 패싯에 없어 불러온 상품으로 센다 */
export function brandHubProductStatsInfo(on: BrandHubScopeOn, region: BrandHubProductRegion): string[] {
  return ['평균 할인율: 불러온 상품 기준', ...brandHubProductInfo(on, region)];
}

/**
 * 캠페인 구역 — 넘칠 때만 「{N}개 중 30개」.
 * 🔑 받기는 넉넉히 받고(총수를 알아야 한다) **그리는 것은 다른 구역과 같은 수**다.
 */
export function brandHubEventsHint(count: number): string {
  return count > BRAND_HUB_FACE_CARDS ? `${fNumber(count)}개 중 ${fNumber(BRAND_HUB_FACE_CARDS)}개` : '';
}

/**
 * 캠페인 구역 ⓘ — 출처 · 세는 규칙 · 못 거는 축(제품군·채널·페르소나).
 * @param sourceNote `eventSourceNote` 의 값(「10월 1일 기준 · KR 캠페인만」)
 */
export function brandHubEventsInfo(on: BrandHubScopeOn, sourceNote: string): string[] {
  return [
    sourceNote,
    '브랜드가 낸 글(공식·광고·협찬)이 근거인 캠페인만',
    ...brandHubMissInfo(on, ['제품군', '채널', '페르소나']),
  ].filter(Boolean);
}

/**
 * 「같은 기간 상위 브랜드」 ⓘ — 같은 조건 · 순위에서 뺀 브랜드.
 * ⚠️ 이름으로 찾으면 그 브랜드는 그대로 나온다 — 순위에서만 뺀다.
 */
export function brandHubPeersInfo(excluded: readonly BrandQualityFlag[]): string[] {
  const names = excluded.slice(0, PEER_EXCLUDED_NAMES).map((row) => row.brand);
  const more = excluded.length - names.length;
  return [
    '위 필터와 같은 조건',
    ...(excluded.length > 0
      ? [
          `같은 문구 반복 게시 브랜드 ${fNumber(excluded.length)}개 순위 제외`,
          `제외: ${names.join('·')}${more > 0 ? ` 외 ${fNumber(more)}개` : ''}`,
        ]
      : []),
  ];
}
